<script setup lang="ts">
import { computed } from 'vue'
import { CrashLinkStatus } from '@roadfootage/api-client'
import type { RecordedCrashDto } from '@roadfootage/api-client'
import {
  formatCrashDateTime,
  formatCrashSeverity,
  formatCrashSource,
  formatCrashType,
  formatDate,
  formatNumber
} from '@/utils/format'
import type { CrashLinkDecision } from '@/utils/crash-link-patch'

// Staff view of the recorded crash an uploader linked to their report (RF-070). A recorded crash is open data, not footage,
// so the panel says so in its title. While the link is pending, staff keep it or unlink it; approving the report applies that.
const props = defineProps<{
  /** The crash, or null when it is still loading or could not be loaded. */
  crash: RecordedCrashDto | null
  status: CrashLinkStatus | null | undefined
  decision: CrashLinkDecision
  reviewedAt?: Date | string | null
}>()

const emit = defineEmits<{ 'update:decision': [value: CrashLinkDecision] }>()

const isPending = computed(() => props.status === CrashLinkStatus.Pending)

const statusText = computed(() => {
  if (isPending.value) return props.decision === 'unlink' ? 'WILL BE REJECTED ON SAVE' : 'PENDING'
  const date = formatDate(props.reviewedAt)
  const label = props.status === CrashLinkStatus.Approved ? 'APPROVED' : 'REJECTED'
  return date ? `${label} ${date}` : label
})

const statusColor = computed(() => {
  if (isPending.value && props.decision === 'unlink') return 'negative'
  if (props.status === CrashLinkStatus.Approved) return 'positive'
  if (props.status === CrashLinkStatus.Rejected) return 'grey-7'
  return 'warning'
})

const sourceAndDate = computed(() => {
  const c = props.crash
  if (!c) return ''
  return `${formatCrashSource(c.source)} · ${formatCrashDateTime(c.crashDateTime, c.crashTimezone, c.dateTimePrecision)}`
})

const fatalities = computed(() => {
  const n = props.crash?.fatalityCount
  if (n === null || n === undefined) return 'fatalities unknown'
  return `${formatNumber(n)} ${n === 1 ? 'fatality' : 'fatalities'}`
})

const severityLine = computed(
  () => `${formatCrashSeverity(props.crash?.severity)} · ${fatalities.value}`
)

const typeAndAddress = computed(() => {
  const address = props.crash?.address?.formattedAddress
  return `${formatCrashType(props.crash?.crashType)} · ${address || 'Address unknown'}`
})

const options = [
  { label: 'Keep link', value: 'keep' },
  { label: 'Unlink', value: 'unlink' }
]
</script>

<template>
  <q-banner dense rounded class="bg-amber-1 text-grey-10 q-mt-sm" data-testid="crash-link-panel">
    <template #avatar>
      <q-icon name="mdi-alert-circle-outline" color="warning" />
    </template>

    <div class="text-weight-bold text-uppercase" data-testid="crash-link-title">
      Recorded crash, not footage
    </div>
    <div class="q-mt-xs">
      Link:
      <q-badge :color="statusColor" class="q-ml-xs" data-testid="crash-link-status">
        {{ statusText }}
      </q-badge>
    </div>

    <template v-if="crash">
      <div class="q-mt-xs" data-testid="crash-link-source-date">{{ sourceAndDate }}</div>
      <div data-testid="crash-link-severity">{{ severityLine }}</div>
      <div data-testid="crash-link-type-address">{{ typeAndAddress }}</div>
    </template>
    <div v-else class="q-mt-xs text-grey-8" data-testid="crash-link-unavailable">
      Crash details unavailable (id kept)
    </div>

    <template v-if="isPending">
      <q-option-group
        :model-value="decision"
        :options="options"
        type="radio"
        inline
        dense
        data-testid="crash-link-decision"
        @update:model-value="emit('update:decision', $event as CrashLinkDecision)"
      />
      <div class="text-caption text-grey-8">Approving the report approves the link.</div>
    </template>
  </q-banner>
</template>
